import { client } from "@/hono";
import { i18n } from "@msuite/ui/localization";
import { isLocale } from "@msuite/utils/locales";
import { useEffect, useRef } from "react";
import { useAuth } from "./use-auth";

/**
 * Keep the interface language of this browser and the one stored on the signed-in user in step.
 *
 * The server wins once it holds a value: a stored `users.language` that differs from what this
 * browser shows is applied here and written to `localStorage`, without a reload, so a language
 * chosen on another device follows the user. The browser wins only while the server holds none:
 * a user who picked a language before the column existed has it in `localStorage` alone, and
 * that choice is posted once, so the push texts and e-mails the server writes reach them in it.
 *
 * Nothing runs while no user is signed in, and a mount posts at most once — the session refetch
 * that follows a post must not send it again.
 */
export function useLanguageSync() {
	/** Context */
	const { user } = useAuth();

	/** State */
	const hasPosted = useRef(false);

	/** Effects */
	useEffect(() => {
		if (!user?.id) return;
		const serverLanguage = user.language;
		if (isLocale(serverLanguage)) return applyServerLanguage(serverLanguage);
		if (serverLanguage || hasPosted.current) return;
		const storedLanguage = readStoredLanguage();
		if (!isLocale(storedLanguage)) return;
		hasPosted.current = true;
		void client.users.language.$post({ json: { language: storedLanguage } });
	}, [user?.id, user?.language]);
}

/** Show the language the server holds, and remember it for the next start of this browser. */
function applyServerLanguage(language: string) {
	if (i18n.language === language) return;
	i18n.changeLanguage(language);
	writeStoredLanguage(language);
}

/** The language this browser remembers; `null` where storage is unavailable. */
function readStoredLanguage() {
	try {
		return localStorage.getItem("language");
	} catch {
		return null;
	}
}

/** Remember a language for this browser; a storage that refuses the write changes nothing. */
function writeStoredLanguage(language: string) {
	try {
		localStorage.setItem("language", language);
	} catch {
		// Private windows and blocked site data refuse writes; the session still carries the value.
	}
}
